
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import UIkit from "uikit";
import FabricStatusService from "@/services/fabric_status_service";
import {
  IFabricStatus,
  StatusVisibility,
  VISIBILITY_OPTIONS,
  visibilityLabel,
} from "@/models/fabric_status";

/**
 * Status picker: a dropdown of existing labels, plus inline create/edit.
 *
 * The component emits the HANDLE, never the label — the handle is what a fabric
 * stores, and it is stable across a rename. Staff only ever see labels.
 */
@Component
export default class FabricStatusPicker extends Vue {
  /** The fabric's current status HANDLE. */
  @Prop({ default: "" }) readonly value!: string;

  private service = new FabricStatusService();

  protected statuses: IFabricStatus[] = [];
  protected loading = false;
  protected saving = false;
  protected formOpen = false;
  protected editing: IFabricStatus | null = null;

  protected visibilityOptions = VISIBILITY_OPTIONS;
  protected visibilityLabel = visibilityLabel;

  protected form: { label: string; note: string; visibility: StatusVisibility } = {
    label: "",
    note: "",
    visibility: StatusVisibility.Staff,
  };

  async created(): Promise<void> {
    await this.load();
  }

  private async load(force = false): Promise<void> {
    this.loading = true;
    try {
      this.statuses = await this.service.list(force);
    } catch (e) {
      this.statuses = [];
    } finally {
      this.loading = false;
    }
    // The vocabulary arrives AFTER the first render, so the <select> initially
    // holds nothing but the disabled placeholder. Vue set its DOM value to the
    // fabric's handle then, but no such <option> existed yet, leaving the element
    // with selectedIndex -1; once the options patch in, the browser falls back to
    // the first selectable one — "Active" — and Vue won't re-apply the value,
    // because from its side the bound value never changed. So the dropdown claims
    // a status the fabric doesn't have, and saving would make that lie true.
    // Re-assert it by hand now that the options exist.
    await this.$nextTick();
    this.syncSelect();
  }

  /** Push `value` back onto the DOM element, whatever the browser chose. */
  private syncSelect(): void {
    const el = this.$refs.select as HTMLSelectElement | undefined;
    if (el) el.value = this.value || "";
  }

  // Same hazard from the other direction: the parent may resolve the fabric after
  // this component has mounted and its options are already in place.
  @Watch("value")
  protected onValueChange(): void {
    this.$nextTick(() => this.syncSelect());
  }

  /**
   * The vocabulary, plus a placeholder for a handle that isn't in it — a status
   * deleted out from under a fabric, or one from a stale cache. Without this the
   * fabric would silently render as whichever status happens to sort first, which
   * is the same misrepresentation the sync above exists to prevent.
   */
  get options(): IFabricStatus[] {
    if (!this.value || this.loading) return this.statuses;
    if (this.statuses.some((s) => s.handle === this.value)) return this.statuses;
    return [
      ...this.statuses,
      { handle: this.value, label: `${this.value} (unknown status)` } as IFabricStatus,
    ];
  }

  get selected(): IFabricStatus | null {
    return this.statuses.find((s) => s.handle === this.value) || null;
  }

  get visibilityHint(): string {
    return VISIBILITY_OPTIONS.find((v) => v.value === this.form.visibility)?.hint || "";
  }

  /** Mirrors the API's handle derivation, so the user sees what will be stored. */
  get previewHandle(): string {
    return this.form.label
      .normalize("NFKD")
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
  }

  protected onSelect(e: Event): void {
    const val = (e.target as HTMLSelectElement).value;
    if (val === "__new__") {
      // Reset the <select> back to the real value — "＋ New status…" is an action,
      // not a selectable status, and leaving it selected would misrepresent state.
      (e.target as HTMLSelectElement).value = this.value || "";
      this.startCreate();
      return;
    }
    this.$emit("input", val);
  }

  protected startCreate(): void {
    this.editing = null;
    this.form = { label: "", note: "", visibility: StatusVisibility.Staff };
    this.formOpen = true;
  }

  protected startEdit(): void {
    if (!this.selected) return;
    this.editing = this.selected;
    this.form = {
      label: this.selected.label,
      note: this.selected.note || "",
      visibility: this.selected.visibility,
    };
    this.formOpen = true;
  }

  protected closeForm(): void {
    this.formOpen = false;
    this.editing = null;
  }

  protected async submit(): Promise<void> {
    const label = this.form.label.trim();
    if (!label) return;
    this.saving = true;
    try {
      if (this.editing && this.editing._id) {
        await this.service.update(this.editing._id, {
          label,
          note: this.form.note.trim(),
          visibility: this.form.visibility,
        });
        await this.load(true);
        UIkit.notification({ message: `Status “${label}” updated.`, status: "success" });
      } else {
        const created = await this.service.create({
          label,
          note: this.form.note.trim() || undefined,
          visibility: this.form.visibility,
        });
        await this.load(true);
        // Select the status the user just created — they made it in order to use it.
        this.$emit("input", created.handle);
        UIkit.notification({ message: `Status “${label}” created.`, status: "success" });
      }
      this.closeForm();
    } catch (err: any) {
      UIkit.notification({
        message: err?.response?.data?.message || "Could not save the status.",
        status: "danger",
      });
    } finally {
      this.saving = false;
    }
  }

  protected async removeStatus(): Promise<void> {
    if (!this.editing?._id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the status “${this.editing.label}”?`)) return;
    this.saving = true;
    try {
      const wasSelected = this.editing.handle === this.value;
      await this.service.remove(this.editing._id);
      await this.load(true);
      if (wasSelected) this.$emit("input", "");
      UIkit.notification({ message: "Status deleted.", status: "success" });
      this.closeForm();
    } catch (err: any) {
      // The API refuses to delete a status still in use, and says how many
      // fabrics are in the way — surface that verbatim, it's the actionable bit.
      UIkit.notification({
        message: err?.response?.data?.message || "Could not delete the status.",
        status: "danger",
      });
    } finally {
      this.saving = false;
    }
  }
}
